import { Suspense } from "react";
import { Outlet } from "react-router";
import { useAtomValue } from "jotai";
import Sidebar from "./Sidebar";
import { RouteFallback } from "./RouteFallback";
import WorkerStatus from "./WorkerStatus";
import DevMenu from "./DevMenu";
import { localeAtom } from "../stores/locale";
import { t } from "../lib/i18n";
import { useGalaxyInit } from "../lib/hooks/useGalaxyInit";
import { useNavDrawer } from "../lib/hooks/useNavDrawer";

// CareerLayout is the chrome for the user-scoped Career Orbit (/me/career).
// The URL carries no :account because career is a property of the PERSON, not a
// galaxy — the page's data is keyed off the JWT identity. But the chrome is the
// SAME operational galaxy chrome as everywhere else: it renders the real
// <Sidebar/>, which falls back to selectedAccountAtom (the URL→atom mirror,
// sanctioned for chrome by galaxy-url-truth) when the URL has no :account — so
// the left menu stays the currently-selected galaxy's normal menu instead of
// collapsing to a single Career item. Career is not a galaxy nav row (entry is
// the identity/AccountMenu), so the menu just shows the galaxy you were last in.
//
// Unlike the operational Layout, this layout runs NONE of the galaxy-data effects
// (onboarding check, seat gate, unknown-galaxy redirect): those key off a URL
// :account and would bounce /me/career away or fetch wrong-galaxy data.
export default function CareerLayout() {
  useGalaxyInit();
  const { open: sidebarOpen, setOpen: setSidebarOpen } = useNavDrawer();
  const locale = useAtomValue(localeAtom);

  return (
    <div className="relative flex h-screen" style={{ backgroundColor: "var(--bg-void)" }}>
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div
        className={`fixed inset-y-0 left-0 z-30 w-64 transform transition-transform lg:static lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <Sidebar onClose={() => setSidebarOpen(false)} />
      </div>

      <main className="relative z-10 flex-1 overflow-auto">
        <WorkerStatus />
        <div
          className="flex items-center gap-md border-b p-lg lg:hidden"
          style={{ borderColor: "var(--border)" }}
        >
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label={t("archiveLayout.openMenu", locale)}
            className="transition-colors"
            style={{ color: "var(--text-muted)" }}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <span
            style={{
              fontFamily: "var(--display)",
              fontSize: "1.0625rem",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: "var(--text-strong)",
            }}
          >
            OrbitLens{" "}
            <span style={{ fontWeight: 600, color: "var(--gold)" }}>Ace</span>
          </span>
        </div>

        <div className="p-lg sm:p-2xl lg:p-3xl">
          {/* Localized route-level Suspense for the lazy() CareerPage chunk —
              the galaxy chrome stays painted while it loads (manifest §3). */}
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </div>
      </main>

      <DevMenu />
    </div>
  );
}
